import { v2Api } from './v2Client';

const post = (url, body) => v2Api.post(url, body ?? {}).then((r) => r.data);
const arr = (data) => (Array.isArray(data) ? data : []);

/**
 * Permissions d'un staff de tournoi officiel (docs/OFFICIAL_TOURNAMENTS.md § 2.1), noms du DTO, dans l'ordre des bits.
 * Un adjoint n'a jamais `draw` ni `manage_staff`.
 */
export const TOURNAMENT_PERMISSIONS = ['schedule', 'courts', 'checkin', 'referee', 'results', 'draw', 'manage_staff'];

export const TOURNAMENT_PERMISSION_LABELS = {
  schedule: 'Horaires',
  courts: 'Terrains',
  checkin: 'Accueil',
  referee: 'Arbitrage',
  results: 'Résultats',
  draw: 'Tirage',
  manage_staff: 'Équipe',
};

/** Libellé du rôle d'un compte sur un tournoi officiel (`role` de `my`, du staff ou de `bundle.access`). */
export const TOURNAMENT_ROLE_LABELS = {
  responsable: 'Responsable',
  adjoint: 'Adjoint',
  league: 'Ligue',
  organizer_staff: 'Staff du club organisateur',
  club_staff: 'Staff du club',
};

/** Messages des refus d'écriture sur un tournoi officiel (403 `{ error }`, docs/OFFICIAL_TOURNAMENTS.md § 2.2). */
export const OFFICIAL_TOURNAMENT_ERRORS = {
  tournament_forbidden: 'Tu n’as pas le droit de faire cette action sur ce tournoi.',
  field_forbidden: 'Ce champ est géré par la Ligue ou par un autre membre du staff.',
  tournament_closed: 'Le tournoi est clôturé : seule la Ligue peut encore le modifier.',
  permission_not_grantable: 'Cette permission ne peut pas être donnée à un adjoint.',
  already_staff: 'Ce joueur fait déjà partie du staff du tournoi.',
  already_registered: 'Ce joueur est déjà inscrit à ce tournoi.',
  not_affiliated: 'Ce joueur n’est pas affilié à la Ligue cette saison.',
};

/** Message d'une erreur d'API de tournoi officiel (code connu, sinon message de l'API, sinon `fallback`). */
export function officialTournamentErrorMessage(error, fallback = 'Action impossible.') {
  const data = error?.response?.data;
  const code = data?.error;
  if (code && OFFICIAL_TOURNAMENT_ERRORS[code]) return OFFICIAL_TOURNAMENT_ERRORS[code];
  return (typeof data === 'string' && data) || data?.message || error?.message || fallback;
}

/**
 * Routes `/OfficialTournament/*` du responsable, des adjoints et du staff (docs/OFFICIAL_TOURNAMENTS.md § 3.2). Le staff
 * travaille dans le club de son jeton (`X-Club` = son club) : l'API élève chaque requête vers le club organisateur.
 */
export const officialTournamentService = {
  /** Tournois officiels où le compte a des droits effectifs (tous clubs) : `[{ id, name, status, start_date, end_date,
   * organizer_club, role, permissions[], closed_at }]`. */
  my: () => v2Api.get('/OfficialTournament/my').then((r) => arr(r.data?.tournaments)),
  staffList: (tournamentId) => post('/OfficialTournament/staff_list', { tournament_id: tournamentId }).then(arr),
  /** Nomme ou modifie un adjoint : `{ tournament_id, id?, account_id? | player_id?, permissions, note?, active? }`. */
  staffUpsert: (payload) => post('/OfficialTournament/staff_upsert', payload),
  staffRevoke: (id) => post('/OfficialTournament/staff_revoke', { id }),
  /** Joueurs affiliés de tous les clubs (20 max) : `[{ account_id, player_id, name, club }]`. */
  staffCandidates: (tournamentId, q) => post('/OfficialTournament/staff_candidates', { tournament_id: tournamentId, q: q || '' }).then(arr),
  /** Inscription sur place (`checkin` ou Ligue) : joueurs affiliés de la saison, tous clubs (20 max) :
   * `[{ account_id, player_id, name, picture, club: { id, slug, name }, licence_number }]`. L'inscription passe ensuite
   * par POST /TournamentParticipant/register `{ tournament_id, player_id, category_id? }`. */
  registrationCandidates: (tournamentId, q) =>
    post('/OfficialTournament/registration_candidates', { tournament_id: tournamentId, q: q || '' }).then(arr),
  /** Terrains actifs de l'organisateur + blocages du tournoi : `{ terrains: [{ id, number, name, sport }], blocks: [] }`,
   * normalisé en `{ courts, blocks }` (une liste seule, ou `courts` au lieu de `terrains`, est aussi acceptée). */
  courts: (tournamentId) =>
    post('/OfficialTournament/courts', { tournament_id: tournamentId }).then((data) => ({
      courts: arr(Array.isArray(data) ? data : data?.courts ?? data?.terrains),
      blocks: arr(data?.blocks),
    })),
  /** Bloque des terrains de l'organisateur : `{ tournament_id, id?, start_at, end_at, terrain_ids?, reason? }`
   * (`terrain_ids` vide = tout le club). */
  courtBlockUpsert: (payload) => post('/OfficialTournament/court_block_upsert', payload),
  courtBlockDelete: (id) => post('/OfficialTournament/court_block_delete', { id }),
  /** Arbitres possibles : participants, staff du tournoi, membres actifs de l'organisateur. */
  refereeCandidates: (tournamentId, q) =>
    post('/OfficialTournament/referee_candidates', { tournament_id: tournamentId, q: q || '' }).then(arr),
  /** Désigne l'arbitre d'un match (`account_id` ou `player_id`) ; l'API crée son adhésion de passage si besoin. */
  assignReferee: (matchId, { accountId, playerId } = {}) =>
    post('/OfficialTournament/assign_referee', {
      match_id: matchId,
      ...(accountId ? { account_id: accountId } : {}),
      ...(!accountId && playerId ? { player_id: playerId } : {}),
    }),
  checkIn: (participantId) => post('/OfficialTournament/check_in', { participant_id: participantId }),
  undoCheckIn: (participantId) => post('/OfficialTournament/undo_check_in', { participant_id: participantId }),
};
